// src/public/components/FontSizeGuide.jsx
import { X } from "lucide-react";
import { useEffect, useRef } from "react";

const FontSizeGuide = ({ isOpen, onClose, language }) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement;

      // Disable scrolling on body
      document.body.style.overflow = "hidden";

      // Handle scrollbar width
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Set initial focus to the modal
      modalRef.current?.focus();

      // Add keyboard event listener
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClose();
          return;
        }

        if (e.key === "Tab") {
          // Get all focusable elements in the modal
          const focusableElements = modalRef.current?.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );

          if (!focusableElements?.length) return;

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          // If shift + tab
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          }
          // If just tab
          else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";

        // Restore focus to the previous element
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const content = {
    en: {
      title: "How to Change Text Size",
      intro:
        "Now with this responsive web design, you can easily change the text size to fit your eyesight needs.",
      shortcutTitle: "Shortcut",
      shortcutText:
        "If you browse this webpage on a desktop computer, a modern web browser will allow you to hold down the Ctrl key (Command key on a Macintosh) and press the + or - key to increase or decrease the text size, and 0 to go back to the default size. On a smartphone or a tablet, you can simply pinch open on the screen to zoom in (i.e. to enlarge) and pinch close to zoom out (i.e. to reduce) everything.",
      menuTitle: "Menu",
      menuText:
        "You may also change the text size from the menu by following these instructions:",
      chromeTitle: "Chrome",
      chromeText: "On the Page menu, select Zoom, then Larger.",
      firefoxTitle: "Firefox",
      firefoxText:
        "On the View menu, select Zoom, then Zoom In. Or you may click on the Menu icon and select + or - to make the text size bigger or smaller.",
      edgeTitle: "Edge",
      edgeText: "On the Page menu, select Zoom, then + to enlarge.",
      safariTitle: "Safari",
      safariText: "On the View menu, select Make Text Bigger.",
      disclaimerTitle: "Endorsement Disclaimer",
      disclaimerText:
        "Land Registry does not endorse specific web browsers or recommend one browser over another. While some popular browsers are included here, mention of a specific browser does not imply any endorsement or recommendation.",
    },
    tc: {
      title: "如何變更字型大小",
      intro: "採用了適應性網頁設計後，你可簡易地變更字型大小，配合個人需要。",
      shortcutTitle: "快速鍵",
      shortcutText:
        "假如你使用桌上型電腦瀏覽此網頁，近代的瀏覽器可讓用家按著Ctrl鍵（在Macintosh鍵盤上是Command鍵）和+／-鍵來放大或縮小字型，而按著Ctrl鍵和0則可回復預設大小。",
      menuTitle: "選項單",
      menuText:
        "使用智能電話和平板電腦時，你只需在螢幕上滑動手指便能放大或縮小圖文。",
      chromeTitle: "Chrome",
      chromeText: "在頁面的選項單選擇「縮放」，然後按「+」放大。",
      firefoxTitle: "Firefox",
      firefoxText:
        '在"檢視"選單中，選擇"縮放"，然後選擇"放大"。或者您可以點擊選單圖標，選擇"+"或"-"來調整文字大小。',
      edgeTitle: "Edge",
      edgeText: '在"頁面"選單中，選擇"縮放"，然後選擇"+"來放大。',
      safariTitle: "Safari",
      safariText: "在「檢視」一欄選擇「放大文字」。",
      disclaimerTitle: "免責聲明",
      disclaimerText:
        "土地註冊處並沒有認可特定的瀏覽器或建議使用哪一個瀏覽器。本文內容包括部分常用的瀏覽器，但提及某一個瀏覽器並沒有暗示任何認可或建議的意思。",
    },
    sc: {
      title: "如何变更字型大小",
      intro: "采用了适应性网页设计后，你可简易地变更字型大小，配合个人需要。",
      shortcutTitle: "快速键",
      shortcutText:
        "假如你使用桌上型电脑浏览此网页，近代的浏览器可让用家按着Ctrl键（在Macintosh键盘上是Command键）和+／-键来放大或缩小字型，而按着Ctrl键和0则可回复预设大小。",
      menuTitle: "选项单",
      menuText:
        "使用智能电话和平板电脑时，你只需在萤幕上滑动手指便能放大或缩小图文。",
      chromeTitle: "Chrome",
      chromeText: "在页面的选项单选择「缩放」，然后按「+」放大。",
      firefoxTitle: "Firefox",
      firefoxText:
        '在"查看"菜单中，选择"缩放"，然后选择"放大"。或者您可以点击菜单图标，选择"+"或"-"来调整文字大小。',
      edgeTitle: "Edge",
      edgeText: '在"页面"菜单中，选择"缩放"，然后选择"+"来放大。',
      safariTitle: "Safari",
      safariText: "在「检视」一栏选择「放大文字」。",
      disclaimerTitle: "免责声明",
      disclaimerText:
        "土地注册处并没有认可特定的浏览器或建议使用哪一个浏览器。本文内容包括部分常用的浏览器，但提及某一个浏览器并没有暗示任何认可或建议的意思。",
    },
  };

  const currentContent = content[language] || content.en;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      ref={modalRef}
      tabIndex="-1"
    >
      <div className="min-h-screen py-8 px-4 flex items-center justify-center w-full">
        <div
          className="bg-white rounded-lg w-full max-w-lg relative"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky Header */}
          <div className="sticky top-0 bg-white rounded-t-lg border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">{currentContent.title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="px-6 py-4 overflow-y-auto">
            <div className="space-y-6">
              <p className="leading-relaxed">{currentContent.intro}</p>

              <section>
                <h3 className="font-bold mb-3">
                  {currentContent.shortcutTitle}
                </h3>
                <p className="leading-relaxed">{currentContent.shortcutText}</p>
              </section>

              <section>
                <h3 className="font-bold mb-3">{currentContent.menuTitle}</h3>
                <p className="leading-relaxed">{currentContent.menuText}</p>
              </section>

              <section>
                <h3 className="font-bold mb-3">{currentContent.chromeTitle}</h3>
                <p className="leading-relaxed">{currentContent.chromeText}</p>
              </section>

              <section>
                <h3 className="font-bold mb-3">
                  {currentContent.firefoxTitle}
                </h3>
                <p className="leading-relaxed">{currentContent.firefoxText}</p>
              </section>

              <section>
                <h3 className="font-bold mb-3">{currentContent.edgeTitle}</h3>
                <p className="leading-relaxed">{currentContent.edgeText}</p>
              </section>

              <section>
                <h3 className="font-bold mb-3">{currentContent.safariTitle}</h3>
                <p className="leading-relaxed">{currentContent.safariText}</p>
              </section>

              <section className="border-t pt-4">
                <h3 className="font-bold mb-3">
                  {currentContent.disclaimerTitle}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {currentContent.disclaimerText}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontSizeGuide;
